export type SortDirection = 'asc' | 'desc';
export type SortConfig = { columnIndex: number; direction: SortDirection } | null;

// Parses a display string from an analytics table cell into a value comparable for sorting.
const parseSortValue = (raw: unknown): number | string | null => {
  if (raw === null || raw === undefined) return null;
  const str = String(raw).trim();
  if (str === '' || str === '-') return null;

  const numMatch = str.match(/^-?\d+(?:\.\d+)?/);
  if (numMatch) {
    const n = parseFloat(numMatch[0]);
    if (!Number.isNaN(n)) return n;
  }

  return str.toLowerCase();
};

// Compare two display strings according to direction. Null/dash values always sink to the bottom
export const compareValues = (a: unknown, b: unknown, direction: SortDirection): number => {
  const va = parseSortValue(a);
  const vb = parseSortValue(b);

  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;

  const aIsNum = typeof va === 'number';
  const bIsNum = typeof vb === 'number';

  let result: number;
  if (aIsNum && bIsNum) {
    result = (va as number) - (vb as number);
  } else if (aIsNum) {
    // Numbers come before strings for stable mixed-type ordering.
    result = -1;
  } else if (bIsNum) {
    result = 1;
  } else {
    result = (va as string).localeCompare(vb as string);
  }

  return direction === 'asc' ? result : -result;
};

// Toggle a column's sort state between ascending and descending. First click on a column starts
// with ascending; subsequent clicks toggle direction.
export const nextSortConfig = (prev: SortConfig, columnIndex: number): SortConfig => {
  if (!prev || prev.columnIndex !== columnIndex) {
    return { columnIndex, direction: 'asc' };
  }
  return { columnIndex, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
};
